import React, { useState, useRef, useEffect } from 'react';
import { track } from '@lf/analytics-events';
import { stream_post } from '@lf/api-client';
import { processText } from '@lf/api-client/stream';
import AnimatingDot from '../../components/AnimatingDot';
import LatexRenderer from '../../components/LatexRenderer';
import Speech from './Speech';
import FeedbackButton from '../../components/FeedbackButton';

const _sendChatMessage = async (chatId, message) => {
  try {
    return await stream_post('/chat/question', {
      chat_id: chatId,
      message: message
    });
  } catch (error) {
    console.error('Error sending chat message:', error);
    return '';
  }
};

const _streamChatSpeech = async (chatId) => {
  try {
    return await stream_post('/chat/speech', {
      chat_id: chatId
    });
  } catch (error) {
    console.error('Error streaming chat speech:', error);
    return '';
  }
};

const VideoChat = ({ question }) => {
  const initialMessages = (question?.messages || []).filter(m => m.role !== 'system');

  const [chatHistory, setChatHistory] = useState(initialMessages.map((message) => ({ sender: message.role, text: message.message })));
  const [userMessage, setUserMessage] = useState('');
  const [isAssistantLoading, setIsAssistantLoading] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('Ask a question about the video...');
  const chatContainerRef = useRef(null);

  // Scroll to bottom when chat history changes
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Scroll to bottom when component mounts
  useEffect(() => {
    scrollToBottom();
  }, []);

  // Fire `qna_completed` on unmount (user navigated away or closed
  // the chat). Reads message count from the latest history via a ref
  // so we don't re-register the unmount handler on every keystroke.
  const messageCountRef = useRef(0);
  useEffect(() => {
    messageCountRef.current = chatHistory.length;
  }, [chatHistory]);
  useEffect(() => {
    return () => {
      if (question?.chat_id) {
        track('qna_completed', {
          chat_id: question.chat_id,
          message_count: messageCountRef.current,
          ended_by: 'navigation',
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!question) {
    return null;
  }

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  async function handleSendMessage(event) {
    event.preventDefault();

    if (!userMessage.trim() || isAssistantLoading) return;

    const message = userMessage;
    setUserMessage('');
    setPlaceholderText('');
    setIsAssistantLoading(true);

    // Add user message to chat history
    const userMessageObj = { id: Date.now(), sender: 'user', text: message };
    setChatHistory(prev => {
      const next = [...prev, userMessageObj];
      track('qna_message_sent', {
        chat_id: question.chat_id,
        // user-message ordinal — count of `user` messages including
        // this one. Server-side ordering uses the same convention.
        message_index: next.filter((m) => m.sender === 'user').length - 1,
      });
      return next;
    });

    try {
      // Add assistant message placeholder
      const assistantMessageId = Date.now() + 1;
      setChatHistory(prev => [...prev, { id: assistantMessageId, sender: 'assistant', text: '' }]);

      // Stream the response
      const res = await _sendChatMessage(question.chat_id, message);
      let fullText = '';

      await processText(res, (text) => {
        fullText = text;
        setChatHistory(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, text }
              : msg
          )
        );
      });

      // Stream speech if available
      if (fullText) {
        const speechRes = await _streamChatSpeech(question.chat_id);

        // Update the message with speech data
        setChatHistory(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, speechData: speechRes }
              : msg
          )
        );
      }
    } catch (error) {
      console.error('Error in chat:', error);
    } finally {
      setIsAssistantLoading(false);
      setPlaceholderText('Ask a question about the video...');
    }
  }

  function handleMessageChange(event) {
    if (event.target.value.indexOf("\n") >= 0 && event.target.value.trim() !== '') {
      event.preventDefault();
      setUserMessage(event.target.value.replace(/\n/g, ''));
      handleSendMessage({ preventDefault: () => { } });
    } else {
      setUserMessage(event.target.value);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage(event);
    }
  }

  return (
    <div className="flex flex-col">
      {chatHistory.length > 0 && (
        <div ref={chatContainerRef} className="flex-grow overflow-y-auto max-h-[400px]">
          {chatHistory.map((message, index) => (
            <div key={index} className="mb-3">
              {message.sender === 'assistant' && (
                <div className="flex w-full">
                  <div className="w-full">
                    <div className="inline-block p-2 border border-gray-300 rounded-xl bg-white">
                      <LatexRenderer text={message.text} />
                      {index === chatHistory.length - 1 && isAssistantLoading && (
                        <span className="ml-1">
                          <AnimatingDot />
                        </span>
                      )}
                      {index === chatHistory.length - 1 &&
                        !isAssistantLoading && (
                          <Speech
                            getSpeech={async () => _streamChatSpeech(question.chat_id)}
                          />
                        )}
                    </div>
                  </div>
                  {index === chatHistory.length - 1 &&
                    !isAssistantLoading && (
                      <div className="flex flex-col justify-end min-w-8 py-1">
                        <FeedbackButton
                          contentIds={[question.chat_id]}
                          data={{ chat_index: index, kind: "chat" }}
                        />
                      </div>
                    )}
                </div>
              )}
              {message.sender === 'user' && (
                <div className="w-auto max-w-[85%] ml-auto p-3 rounded-xl border border-blue-500 bg-blue-100">
                  <LatexRenderer text={message.text} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSendMessage} className="mt-2">
        <div className="flex">
          <textarea
            placeholder={placeholderText}
            maxLength={300}
            rows={2}
            value={userMessage}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            className="flex-grow p-2 border border-gray-300 rounded"
            disabled={isAssistantLoading}
          />
        </div>
      </form>
    </div>
  );
};

export default VideoChat; 
